// src/features/ownerAdmin/ConfigPage.tsx
import { useState, useEffect } from 'react';
import { useRestartDashboard, useMarketSweepStatus, useUpdateSweepFrequency, useLeaderboardCacheHours, useUpdateLeaderboardCacheHours } from './useCreatorData';
import './OwnerAdminPage.css';

function MarketSweepCard() {
  const { data, isLoading } = useMarketSweepStatus();
  const updateFrequency = useUpdateSweepFrequency();
  const [hoursInput, setHoursInput] = useState('');

  // Only overwrite the input from server data when the user hasn't
  // already started typing a new value — otherwise every 30s refetch
  // would stomp on whatever they're mid-edit on.
  useEffect(() => {
    if (data && hoursInput === '') setHoursInput(String(data.hoursBetweenSweeps));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSave() {
    const hours = parseFloat(hoursInput);
    if (!Number.isFinite(hours) || hours < 1 || hours > 168) return;
    updateFrequency.mutate(hours);
  }

  return (
    <article className="card" style={{ marginTop: '1.25rem' }}>
      <header className="card__header">
        <div>
          <p className="card__eyebrow">Background job</p>
          <h2 className="card__title">Market Depth Sweep</h2>
        </div>
      </header>
      <div className="card__content">
        <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 0.75rem' }}>
          Periodically sweeps every tradeable item for order-book depth data (not just
          alert-watched or profit-calculator items, which are covered separately and more
          frequently). Runs a small, rate-limit-respecting batch every other minute — a full
          sweep of the catalog takes a few hours uninterrupted.
        </p>

        {isLoading || !data ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</p>
        ) : (
          <div style={{ marginBottom: '1rem', fontSize: 13 }}>
            <p style={{ margin: '0 0 0.35rem' }}>
              Status: <strong>{data.status === 'running' ? 'Running' : 'Idle'}</strong>
              {data.status === 'running' && (
                <> — {data.currentPosition} / {data.totalItems} items this sweep
                  ({data.itemsProcessed} processed, {data.itemsFailed} failed so far)</>
              )}
            </p>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              {data.finishedAt
                ? `Last sweep finished: ${new Date(data.finishedAt).toLocaleString()}`
                : 'No sweep has completed yet.'}
            </p>
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 13 }}>
          Start a new sweep every
          <input
            type="number" min={1} max={168} step={0.5}
            value={hoursInput}
            onChange={(e) => setHoursInput(e.target.value)}
            style={{ width: '5rem' }}
          />
          hours (after the previous one finishes)
        </label>
        <button
          className="button-primary"
          style={{ marginTop: '0.75rem' }}
          onClick={handleSave}
          disabled={updateFrequency.isPending}
        >
          {updateFrequency.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </article>
  );
}

function LeaderboardCacheCard() {
  const { data, isLoading } = useLeaderboardCacheHours();
  const updateHours = useUpdateLeaderboardCacheHours();
  const [hoursInput, setHoursInput] = useState('');

  useEffect(() => {
    if (data && hoursInput === '') setHoursInput(String(data.hours));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSave() {
    const hours = parseFloat(hoursInput);
    if (!Number.isFinite(hours) || hours < 1 || hours > 24) return;
    updateHours.mutate(hours);
  }

  return (
    <article className="card" style={{ marginTop: '1.25rem' }}>
      <header className="card__header">
        <div>
          <p className="card__eyebrow">Background caching</p>
          <h2 className="card__title">Leaderboard Cache</h2>
        </div>
      </header>
      <div className="card__content">
        <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 0.75rem' }}>
          Every leaderboard request is cached per game mode, category, and page. Requests within
          this window are served from the cache instead of calling the live API, saving on API
          calls for popular leaderboard pages.
        </p>

        {isLoading || !data ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</p>
        ) : null}

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 13 }}>
          Cache leaderboard data for
          <input
            type="number" min={1} max={24} step={1}
            value={hoursInput}
            onChange={(e) => setHoursInput(e.target.value)}
            style={{ width: '5rem' }}
          />
          hours (1-24)
        </label>
        <button
          className="button-primary"
          style={{ marginTop: '0.75rem' }}
          onClick={handleSave}
          disabled={updateHours.isPending}
        >
          {updateHours.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </article>
  );
}

export function ConfigPage() {
  const [confirming, setConfirming] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const restart = useRestartDashboard();

  function handleRestart() {
    if (!confirming) { setConfirming(true); return; }
    setRestarting(true);
    restart.mutate(undefined, {
      onError: () => setRestarting(false),
    });
  }

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Bot owner only</p>
        <h1 className="page-header__title">Config</h1>
      </div>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">Process control</p>
            <h2 className="card__title">Restart Dashboard</h2>
          </div>
        </header>
        <div className="card__content">
          <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 0.75rem' }}>
            Restarts this dashboard&apos;s own process only — the bot and website are separate
            services and are not affected. The page will be briefly unreachable while it comes
            back up.
          </p>
          <button className="button-danger" onClick={handleRestart} disabled={restarting}>
            {restarting ? 'Restarting…' : confirming ? 'Click again to confirm' : 'Restart dashboard'}
          </button>
          {restarting && (
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: '0.75rem' }}>
              The dashboard is restarting — this page will stop responding for a few seconds, then
              you can refresh.
            </p>
          )}
        </div>
      </article>

      <MarketSweepCard />
      <LeaderboardCacheCard />
    </>
  );
}
